/* global __DEV_REACT_APP_ENV__ */
import runtimeEnv from '@mars/heroku-js-runtime-env'

/**
 * Canonical hosts. Environment is derived from the hostname (see
 * docs/adr/0001-derive-environment-from-hostname.md) rather than from Heroku
 * dyno metadata, which only exists at runtime and was unavailable at build time
 * after the Vite migration.
 */
export const PRODUCTION_HOST = 'actions.bonobolabs.com'
export const STAGING_HOST = 'actions-staging.bonobolabs.com'

/** production | staging | development, from the browser hostname. */
export const deriveEnvironment = (hostname) => {
  if (hostname === PRODUCTION_HOST) {
    return 'production'
  }
  if (hostname === STAGING_HOST) {
    return 'staging'
  }
  return 'development'
}

/**
 * CRA replaced `process.env.REACT_APP_*` in the bundle. Vite inlines the same keys on
 * `process.env.REACT_APP_*` (and `import.meta.env.*`) via `vite.config.js` `define` + `envPrefix`.
 * We read `process.env` here so Jest (Node) can load this module without `import.meta` syntax.
 */
const parseViteDevReactAppEnv = () => {
  try {
    if (typeof __DEV_REACT_APP_ENV__ === 'undefined') {
      return {}
    }
    if (typeof __DEV_REACT_APP_ENV__ === 'string') {
      return __DEV_REACT_APP_ENV__ === ''
        ? {}
        : JSON.parse(__DEV_REACT_APP_ENV__)
    }
    return __DEV_REACT_APP_ENV__
  } catch {
    return {}
  }
}

/** @type {Record<string, string | undefined>} */
const fromBundledProcessEnv = {
  REACT_APP_HEROKU_APP_NAME: process.env.REACT_APP_HEROKU_APP_NAME,
  REACT_APP_HEROKU_RELEASE_VERSION:
    process.env.REACT_APP_HEROKU_RELEASE_VERSION,
  REACT_APP_HEROKU_BRANCH: process.env.REACT_APP_HEROKU_BRANCH,
  REACT_APP_FIREBASE_APIKEY: process.env.REACT_APP_FIREBASE_APIKEY,
  REACT_APP_FIREBASE_AUTHDOMAIN: process.env.REACT_APP_FIREBASE_AUTHDOMAIN,
  REACT_APP_FIREBASE_DATABASEURL: process.env.REACT_APP_FIREBASE_DATABASEURL,
  REACT_APP_FIREBASE_PROJECTID: process.env.REACT_APP_FIREBASE_PROJECTID,
  REACT_APP_FIREBASE_STORAGEBUCKET:
    process.env.REACT_APP_FIREBASE_STORAGEBUCKET,
  REACT_APP_FIREBASE_MESSAGINGSENDERID:
    process.env.REACT_APP_FIREBASE_MESSAGINGSENDERID,
  REACT_APP_FIREBASE_APPID: process.env.REACT_APP_FIREBASE_APPID,
  REACT_APP_HELPSCOUT_BEACON_KEY: process.env.REACT_APP_HELPSCOUT_BEACON_KEY,
  REACT_APP_SENTRY_URL: process.env.REACT_APP_SENTRY_URL,
  REACT_APP_FATHOM: process.env.REACT_APP_FATHOM,
  REACT_APP_AMPLITUDE: process.env.REACT_APP_AMPLITUDE,
  REACT_APP_GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID,
}

/** Drop `undefined` keys so a later source can't clobber an earlier value with nothing. */
const defined = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined))

/** The merged build-time + runtime env. Later sources win, but only for keys they actually set. */
const mergeEnv = () => ({
  ...defined(runtimeEnv()),
  ...defined(parseViteDevReactAppEnv()),
  ...defined(fromBundledProcessEnv),
})

/**
 * Pure resolver: given a hostname and the merged env, derive the full client
 * config. This is the single seam the config tests exercise.
 */
export const resolveConfig = ({ hostname, env }) => {
  const environment = deriveEnvironment(hostname)

  const isFirstParty = environment === 'production' || environment === 'staging'
  const authDomain = isFirstParty ? hostname : env.REACT_APP_FIREBASE_AUTHDOMAIN

  // Best-effort diagnostics: prefer the Heroku app name when present (runtime
  // injection), otherwise derive a stable name from the environment.
  const serverName =
    env.REACT_APP_HEROKU_APP_NAME ?? `actions-web-${environment}`

  return {
    environment,
    serverName,
    appVersion: env.REACT_APP_HEROKU_RELEASE_VERSION,
    branch: env.REACT_APP_HEROKU_BRANCH,
    authDomain,
    firebase: {
      apiKey: env.REACT_APP_FIREBASE_APIKEY,
      databaseURL: env.REACT_APP_FIREBASE_DATABASEURL,
      projectId: env.REACT_APP_FIREBASE_PROJECTID,
      storageBucket: env.REACT_APP_FIREBASE_STORAGEBUCKET,
      messagingSenderId: env.REACT_APP_FIREBASE_MESSAGINGSENDERID,
      appId: env.REACT_APP_FIREBASE_APPID,
      authDomain,
    },
    helpScoutKey: env.REACT_APP_HELPSCOUT_BEACON_KEY,
    sentryURL: env.REACT_APP_SENTRY_URL,
    FathomToken: env.REACT_APP_FATHOM,
    AmplitudeAPIKey: env.REACT_APP_AMPLITUDE,
    GoogleClientID: env.REACT_APP_GOOGLE_CLIENT_ID,
  }
}

const resolved = resolveConfig({
  hostname: window.location.hostname,
  env: mergeEnv(),
})

export const {
  environment,
  serverName,
  appVersion,
  branch,
  firebase,
  helpScoutKey,
  sentryURL,
  FathomToken,
  AmplitudeAPIKey,
  GoogleClientID,
} = resolved
